import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import { schooldetails } from './redux/schooldetails';
import Backimage from './images/anouncement.svg';

import {
  normalizeSchoolOfficeMessages,
  isOfficeMessageUnread,
  officeMessageMatchKey,
} from './utils/officeMessages';

const API_BASE = process.env.REACT_APP_API_URL
  ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
  : 'https://servermain.sisrevolution.com';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function capitalizeWords(str) {
  if (str == null || str === '') return '';
  return String(str)
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatMsgDate(dateVal) {
  if (dateVal == null || dateVal === '') return '—';
  const raw = String(dateVal).trim();
  if (!raw) return '—';
  const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
  const parts = ymd.split('-');
  if (parts.length < 3) return raw;
  const mi = parseInt(parts[1], 10) - 1;
  if (!Number.isFinite(mi) || !MONTHS[mi]) return raw;
  return `${MONTHS[mi]} ${parts[2]}, ${parts[0]}`;
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function MessagesPage() {
  const dispatch = useDispatch();
  const admin = useSelector((state) => state.admin) || {};
  const schooldetails_ = useSelector((state) => state.schooldetails) || [];

  const schoolDoc = schooldetails_[0];
  const [search, setSearch] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [markingRead, setMarkingRead] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const refreshSchool = useCallback(async () => {
    if (!admin.schoolID) return;
    try {
      const res = await axios.post(`${API_BASE}/api/schools/getschooldata/`, {
        schoolID: admin.schoolID,
      });
      if (res.status === 200) {
        dispatch(schooldetails({ data: res.data }));
      }
    } catch {
      setSnackSeverity('error');
      setSnackMsg('Could not load school messages.');
      setSnackOpen(true);
    }
  }, [admin.schoolID, dispatch]);

  useEffect(() => {
    refreshSchool();
  }, [refreshSchool]);

  const officeMessages = useMemo(() => {
    const rows = normalizeSchoolOfficeMessages(schoolDoc?.messages);
    return [...rows].reverse();
  }, [schoolDoc]);

  const filteredMessages = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return officeMessages;
    return officeMessages.filter((m) => {
      const sub = String(m?.subject || '').toUpperCase();
      const body = String(m?.message || '').toUpperCase();
      return sub.includes(q) || body.includes(q);
    });
  }, [officeMessages, search]);

  const unreadCount = useMemo(
    () => officeMessages.reduce((n, m) => (isOfficeMessageUnread(m) ? n + 1 : n), 0),
    [officeMessages]
  );

  const handleSelectMessage = useCallback(
    async (msg) => {
      const key = officeMessageMatchKey(msg);
      setSelectedMessage(msg);

      if (!schoolDoc?._id || !schoolDoc.schoolID) return;
      if (!isOfficeMessageUnread(msg)) return;

      const dt = new Date();
      const time = `${dt.getHours()}:${dt.getMinutes()}:${dt.getSeconds()}`;
      const day = String(dt.getDate()).padStart(2, '0');
      const month = String(dt.getMonth() + 1).padStart(2, '0');
      const year = dt.getFullYear();
      const readDate = `${year}-${month}-${day}`;

      const baseList = normalizeSchoolOfficeMessages(schoolDoc.messages);
      const idx = baseList.findIndex((row) => officeMessageMatchKey(row) === key);
      if (idx === -1) return;

      const updated = baseList.map((row, i) =>
        i === idx ? { ...row, view: ['yes', readDate, time] } : row
      );

      let snapshotMessages;
      try {
        snapshotMessages = JSON.parse(JSON.stringify(baseList));
      } catch {
        snapshotMessages = baseList.map((row) => ({ ...row }));
      }

      const optimisticDoc = { ...schoolDoc, messages: updated };
      dispatch(schooldetails({ data: [optimisticDoc] }));
      setSelectedMessage({ ...msg, view: ['yes', readDate, time] });

      setMarkingRead(true);
      try {
        const res = await axios.post(`${API_BASE}/api/schools/viewmessages/`, {
          _id: schoolDoc._id,
          schoolID: schoolDoc.schoolID,
          message: updated,
        });
        if (res.status === 200 && res.data) {
          const doc = res.data;
          dispatch(schooldetails({ data: Array.isArray(doc) ? doc : [doc] }));
          setSelectedMessage((prev) => {
            if (!prev || officeMessageMatchKey(prev) !== key) return prev;
            return { ...prev, view: ['yes', readDate, time] };
          });
        }
      } catch {
        dispatch(schooldetails({ data: [{ ...schoolDoc, messages: snapshotMessages }] }));
        setSelectedMessage(msg);
        setSnackSeverity('error');
        setSnackMsg('Could not mark message as read.');
        setSnackOpen(true);
      } finally {
        setMarkingRead(false);
      }
    },
    [schoolDoc, dispatch]
  );

  const selectedKey = selectedMessage ? officeMessageMatchKey(selectedMessage) : null;

  if (!admin.schoolID) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">Sign in with a school account to view office messages.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: 'calc(100vh - 120px)' }}>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ py: 2 }} disableGutters>
        <Box sx={{ px: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" component="h1">
              Messages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Announcements from SIS Revolution Office
            </Typography>
            {unreadCount > 0 ? (
              <Chip size="small" color="error" variant="outlined" label={`${unreadCount} unread`} />
            ) : (
              <Typography variant="caption" color="success.main">
                All caught up
              </Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: { xs: '42vh', md: '72vh' } }}>
                <TextField
                  size="small"
                  placeholder="Search subject or body…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ px: 1.5, pt: 1.5, pb: 1, '& fieldset': { border: 'none' } }}
                />
                <Divider />
                <List
                  dense
                  disablePadding
                  sx={{
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: { xs: 220, md: 320 },
                  }}
                >
                  {filteredMessages.length === 0 ? (
                    <Box sx={{ px: 2, py: 6 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {officeMessages.length === 0 ? 'No office messages yet.' : 'No messages match your search.'}
                      </Typography>
                    </Box>
                  ) : (
                    filteredMessages.map((row, index) => {
                      const unread = isOfficeMessageUnread(row);
                      const rowKey = officeMessageMatchKey(row) || `idx-${index}`;
                      const isSel = selectedKey === officeMessageMatchKey(row);
                      return (
                        <React.Fragment key={rowKey}>
                          <ListItemButton
                            selected={isSel}
                            onClick={() => handleSelectMessage(row)}
                            alignItems="flex-start"
                            sx={{
                              py: 1.25,
                              borderLeft: 4,
                              borderColor: unread ? 'primary.main' : 'transparent',
                              bgcolor: unread ? 'action.hover' : 'transparent',
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 46 }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: unread ? 'primary.main' : 'grey.400' }}>
                                  <MarkEmailReadOutlinedIcon sx={{ fontSize: 20 }} />
                                </Avatar>
                                {unread ? (
                                  <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      width: 10,
                                      height: 10,
                                      bgcolor: 'error.main',
                                      borderRadius: '50%',
                                      border: '2px solid',
                                      borderColor: 'background.paper',
                                      boxSizing: 'border-box',
                                    }}
                                  />
                                ) : null}
                              </Box>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    fontWeight: unread ? 700 : 500,
                                    color: unread ? 'primary.main' : 'text.primary',
                                    display: 'block',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {capitalizeWords(row.subject || '') || '(No subject)'}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      mt: 0.25,
                                    }}
                                  >
                                    {row.message || ''}
                                  </Typography>
                                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                                    {formatMsgDate(row.date)}
                                    {unread ? ' · Unread' : ' · Read'}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItemButton>
                          <Divider component="li" />
                        </React.Fragment>
                      );
                    })
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  minHeight: { xs: 320, md: 480 },
                  maxHeight: { xs: '46vh', md: '72vh' },
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {!selectedMessage ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.85 }}>
                    <Box component="img" src={Backimage} alt="" sx={{ width: '100%', maxWidth: 380 }} />
                  </Box>
                ) : (
                  <>
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <ListItemAvatar>
                        <Avatar>
                          <MarkEmailReadOutlinedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight={700}>
                            SIS Revolution Office
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatMsgDate(selectedMessage.date)}
                          </Typography>
                        }
                      />
                      {markingRead ? <CircularProgress size={22} sx={{ ml: 1 }} /> : null}
                    </ListItem>

                    {schoolDoc?.schoolname ? (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        To: <strong>{capitalizeWords(schoolDoc.schoolname)}</strong>
                      </Typography>
                    ) : null}

                    <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                      {capitalizeWords(selectedMessage.subject || '') || '(No subject)'}
                    </Typography>

                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>
                      {selectedMessage.message || ''}
                    </Typography>

                    {!isOfficeMessageUnread(selectedMessage) && Array.isArray(selectedMessage.view) && selectedMessage.view[1] ? (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                        Read on {formatMsgDate(selectedMessage.view[1])}
                        {selectedMessage.view[2] ? ` at ${selectedMessage.view[2]}` : ''}
                      </Typography>
                    ) : null}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
